/*
 * darkTimeWarning — pure helpers for the "this window runs past dark time" advisory (#1119).
 *
 * A fixed-time window ending after the observatory's dark time gets truncated at dispatch: the
 * server clamps `utc_end_time` to the dark-time ceiling, so the tail of the window never runs. On
 * prod 2026-07-23 that was invisible until an EV Lac run had already imaged into twilight.
 *
 * Two surfaces use these, and they must agree:
 *   1. LIVE, while the user picks the window — the ceiling comes from
 *      GET /observatories/:id/dark-time-limit.
 *   2. AFTER SAVE — the server attaches a FIXED_TIME_WINDOW_PAST_DARK_TIME warning to the
 *      POST/PUT response.
 *
 * Both ultimately derive from the server's `observatoryDawnLimit`, the same function the dispatcher
 * clamps against. Never compute dawn in the browser: a second implementation is a second answer.
 */

export const DARK_TIME_WARNING_CODE = 'FIXED_TIME_WINDOW_PAST_DARK_TIME';

/**
 * Warnings attached to a create/update response, as a plain array.
 *
 * apiService returns the whole response body (`{ success, data, warnings? }`), so warnings ride
 * along with the result. Tolerates every "no warnings" shape rather than assuming the key exists.
 */
export function extractWarnings(response) {
  const warnings = response?.warnings;
  return Array.isArray(warnings) ? warnings.filter(Boolean) : [];
}

/** Human-readable messages from a response, for display. */
export function warningMessages(response) {
  return extractWarnings(response)
    .map((w) => (typeof w === 'string' ? w : w?.message))
    .filter(Boolean);
}

/**
 * Minutes by which `fixedTimeEnd` overruns `dawnLimit`; 0 when it fits, when there is no ceiling
 * (site opted out of dark-time constraints or has no location), or when either value is missing or
 * unparseable. Mirrors `darkTimeShortfallMinutes` on the server so the live form warning and the
 * saved-response warning quote the same number.
 */
export function darkTimeOverrunMinutes(fixedTimeEnd, dawnLimit) {
  if (!fixedTimeEnd || !dawnLimit) return 0;
  const end = new Date(fixedTimeEnd).getTime();
  const dawn = new Date(dawnLimit).getTime();
  if (Number.isNaN(end) || Number.isNaN(dawn)) return 0;
  return Math.ceil((end - dawn) / 60000);
}

/** `HH:MM` in UTC — the form and the server warning both speak UTC. */
export function utcHhMm(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(11, 16);
}

/**
 * The live (pre-submit) advisory message, or null when there is nothing to say.
 * Deliberately worded the same as the server's post-save warning.
 */
export function buildDarkTimeWarning({ fixedTimeEnd, dawnLimit, observatoryName } = {}) {
  const overrunMin = darkTimeOverrunMinutes(fixedTimeEnd, dawnLimit);
  if (overrunMin <= 0) return null;
  return `Window ends ${utcHhMm(fixedTimeEnd)}Z but dark time at `
       + `${observatoryName || 'this observatory'} ends ${utcHhMm(dawnLimit)}Z — `
       + `the run will be cut short by about ${overrunMin} min.`;
}
